/**
 * Harper's card on the Team settings sheet: its posts, one card each (Bruno,
 * 2026-10-09). Harper stops being a generalist that works by instructions:
 * the Work log and Deploy notes are opinionated presets that post well with
 * no instructions at all, instructions are an optional layer on top, and a
 * custom post is still there for the Team that wants its own, written from
 * its instructions.
 *
 * Each card says what it is, when and where it posts, whether it is on, and
 * what it last did; its settings fold under it. "+ Custom post" adds a
 * `chronicler.custom` line. Nothing here is new backend: the presets are the
 * `chronicler.work_log` and `chronicler.deploy_notes` kinds, and a custom
 * post's template, instructions, leave-out rule and Approach are its body.
 */
import { useEffect, useState } from "react";
import type { HarperApproach, Job, JobTrigger, TeamJobRunsResponse, WrittenAs } from "@yagni/shared";
import { WRITTEN_AS, WRITTEN_AS_COPY, normalizeWrittenAs, writtenAsNeedsInstructions } from "@yagni/shared";

import { Btn, ChevronRightIcon } from "../../queue/index.js";
import { ApproachRows } from "../ApproachRows.js";
import { formatScheduleSlot } from "../jobSentence.js";
import { PreviewCard, type PreviewInputs } from "../PreviewCard.js";
import { createJob, deleteJob, engageAgent, fetchJobRuns, runJobNow } from "../teamsApi.js";
import type { SettingsCtx } from "./context.js";
import { AreaField, AgentCard, Field, HeadFact, Mark, SelectField, TextField } from "./fields.js";
import { readPath, writePath, type LineDraft } from "./settingsDraft.js";
import { AGENT_DEFAULTS } from "./AgentSections.js";
import { captureUiError } from "../../sentry.js";
import "../teams.css";

const PRESET_KINDS = ["chronicler.work_log", "chronicler.deploy_notes"] as const;
const PRESET_NAME: Record<(typeof PRESET_KINDS)[number], string> = {
  "chronicler.work_log": "Work log",
  "chronicler.deploy_notes": "Deploy notes",
};

/** Harper's lines the sheet draws: the presets first, then custom posts, never a retired kind. */
export function harperPosts(jobs: readonly Job[]): Job[] {
  const presets = PRESET_KINDS.flatMap((kind) => jobs.filter((job) => job.kind === kind));
  const custom = jobs.filter((job) => job.kind === "chronicler.custom" && job.instance !== "default");
  return [...presets, ...custom];
}

function channelLabel(ctx: SettingsCtx, channelId: unknown): string {
  if (typeof channelId !== "string" || !channelId) return ctx.page.channels[0]?.label ?? "this Team's delivery target";
  const known = ctx.page.channels.find((entry) => entry.channelId === channelId);
  if (known) return known.label;
  const slack = ctx.slackChannels.find((entry) => entry.id === channelId);
  return slack ? `#${slack.name}` : channelId;
}

function startsOnDeploy(trigger: JobTrigger): boolean {
  return trigger.type === "event" && trigger.event === "deploy.production";
}

/** "every weekday at 17:00 · #eng". */
export function postWhen(ctx: SettingsCtx, job: Job, draft: LineDraft): string {
  const channel = channelLabel(ctx, readPath(draft.filter, "channel"));
  if (job.kind === "chronicler.deploy_notes") {
    const env = readPath(draft.filter, "environment");
    return `when a ${typeof env === "string" ? env : "production"} deploy lands · ${channel}`;
  }
  const when = startsOnDeploy(draft.trigger)
    ? "when a production deploy lands"
    : draft.trigger.type === "clock"
      ? formatScheduleSlot(draft.trigger.schedule) || "on its rhythm"
      : "on its rhythm";
  return `${job.kind === "chronicler.custom" ? "custom · " : ""}${when} · ${channel}`;
}

/** What the summary line says when the post's runs could not be read. */
export const LAST_POST_UNKNOWN = "last post unknown";

/** The last thing a post did, from its runs: "posted Oct 9, 17:00", or never. */
export function useLastPost(deskId: string, jobId: string): string | null {
  const [line, setLine] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchJobRuns(deskId, jobId)
      .then((response: TeamJobRunsResponse) => {
        if (cancelled) return;
        const at = response.harperRuns
          ? (response.harperRuns.find((run) => run.deliveredAt)?.deliveredAt ?? null)
          : (response.runs.find((run) => run.status === "complete" || run.status === "completed")?.startedAt ?? null);
        setLine(
          at
            ? `posted ${new Date(at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`
            : "never posted",
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        captureUiError(err, { area: "Teams", operation: "settings_read_post_runs", deskId, severity: "warning" });
        setLine(LAST_POST_UNKNOWN);
      });
    return () => {
      cancelled = true;
    };
  }, [deskId, jobId]);
  return line;
}

function channelOptions(ctx: SettingsCtx): Array<{ value: string; label: string }> {
  const seen = new Set<string>();
  const out = [{ value: "", label: "this Team's delivery target" }];
  for (const entry of ctx.page.channels) {
    if (seen.has(entry.channelId)) continue;
    seen.add(entry.channelId);
    out.push({ value: entry.channelId, label: entry.label });
  }
  for (const entry of ctx.slackChannels) {
    if (seen.has(entry.id)) continue;
    seen.add(entry.id);
    out.push({ value: entry.id, label: `#${entry.name}` });
  }
  return out;
}

function previewInputs(body: Record<string, unknown> | undefined): PreviewInputs {
  return {
    instructions: typeof body?.instructions === "string" ? body.instructions : null,
    notRelevant: typeof body?.notRelevant === "string" ? body.notRelevant : null,
    approach: (body?.approach as HarperApproach | undefined) ?? { audience: "auto", length: "auto", groupBy: "auto" },
    category: normalizeWrittenAs(body?.category, "merged_prs"),
  };
}

function PostCard({ ctx, job }: { ctx: SettingsCtx; job: Job }) {
  const draft = ctx.line(job);
  const last = useLastPost(ctx.deskId, job.id);
  const [previewNonce, setPreviewNonce] = useState(0);
  const disabled = ctx.readOnly || ctx.busy;
  const custom = job.kind === "chronicler.custom";
  const name = custom ? job.instance : PRESET_NAME[job.kind as (typeof PRESET_KINDS)[number]] ?? job.kind;
  const set = (update: (d: LineDraft) => LineDraft) => ctx.setLine(job, update);
  const setFilter = (path: string, value: unknown) => set((d) => ({ ...d, filter: writePath(d.filter, path, value) }));
  const setBody = (path: string, value: unknown) => set((d) => ({ ...d, body: writePath(d.body ?? {}, path, value) }));
  const kind = ctx.kindOf(job.kind);
  const category: WrittenAs = normalizeWrittenAs(readPath(draft.body, "category"), "merged_prs");
  const clock = draft.trigger.type === "clock" ? draft.trigger : null;
  const canPost = job.active && (job.kind === "chronicler.work_log" || (custom && job.trigger.type === "clock"));
  const missingInstructions =
    custom && writtenAsNeedsInstructions(category) && !String(readPath(draft.body, "instructions") ?? "").trim();

  return (
    <details className="post" data-testid={`settings-post-${job.id}`}>
      <summary>
        <ChevronRightIcon className="chev" />
        <span className="ph">
          <span className="pr">
            <span className="pt">{name}</span>
            <span className="pm">{postWhen(ctx, job, draft)}</span>
          </span>
          <span className="pl">
            {missingInstructions ? <span style={{ color: "var(--incident)" }}>Say what it reports on before saving.</span> : (last ?? "")}
          </span>
        </span>
        <label className="sw" onClick={(event) => event.stopPropagation()}>
          <input
            type="checkbox"
            checked={draft.active}
            disabled={disabled}
            aria-label={`${name}: ${draft.active ? "on" : "off"}`}
            data-testid={`settings-post-switch-${job.id}`}
            onChange={(event) => set((d) => ({ ...d, active: event.target.checked }))}
          />
        </label>
      </summary>
      <div className="pb">
        {custom && (
          <Field label="What it posts">
            <span className="fact" style={{ fontSize: 12 }}>
              {job.instance}
            </span>
          </Field>
        )}
        <SelectField
          label="Channel"
          value={typeof readPath(draft.filter, "channel") === "string" ? String(readPath(draft.filter, "channel")) : ""}
          disabled={disabled}
          options={channelOptions(ctx)}
          onChange={(value) => setFilter("channel", value || undefined)}
        />
        {job.kind === "chronicler.work_log" && clock && (
          <TextField
            label="Time"
            mono
            value={clock.schedule.times?.[0] ?? ""}
            placeholder="17:00"
            disabled={disabled}
            onChange={(value) =>
              set((d) =>
                d.trigger.type === "clock"
                  ? { ...d, trigger: { ...d.trigger, schedule: { ...d.trigger.schedule, times: value ? [value, ...(d.trigger.schedule.times ?? []).slice(1)] : [] } } }
                  : d,
              )
            }
          />
        )}
        {job.kind === "chronicler.deploy_notes" && (
          <SelectField
            label="Environment"
            value={typeof readPath(draft.filter, "environment") === "string" ? String(readPath(draft.filter, "environment")) : "production"}
            disabled={disabled}
            options={[
              { value: "production", label: "production" },
              { value: "staging", label: "staging" },
            ]}
            onChange={(value) => setFilter("environment", value)}
          />
        )}
        {custom && kind && (
          <SelectField
            label="When it posts"
            value={startsOnDeploy(draft.trigger) ? "deploy.production" : "rhythm"}
            disabled={disabled}
            options={[
              { value: "rhythm", label: "on its rhythm" },
              { value: "deploy.production", label: "when a production deploy lands" },
            ]}
            onChange={(value) =>
              set((d) => ({
                ...d,
                trigger:
                  value === "deploy.production"
                    ? { type: "event", event: "deploy.production" }
                    : job.trigger.type === "clock"
                      ? structuredClone(job.trigger)
                      : structuredClone(kind.defaultTrigger),
              }))
            }
          />
        )}
        {custom && (
          <SelectField
            label="Reports on"
            value={category}
            disabled={disabled}
            options={WRITTEN_AS.map((value) => ({ value, label: WRITTEN_AS_COPY[value].label }))}
            help={WRITTEN_AS_COPY[category].meaning}
            onChange={(value) => setBody("category", value)}
          />
        )}
        {custom ? (
          <AreaField
            label="Instructions"
            rows={2}
            value={String(readPath(draft.body, "instructions") ?? "")}
            placeholder={writtenAsNeedsInstructions(category) ? "What this post reports on, and how." : "Optional. Anything this post should always do or never do."}
            disabled={disabled}
            onChange={(value) => setBody("instructions", value)}
          />
        ) : (
          <AreaField
            label="Instructions"
            rows={2}
            value={draft.note}
            placeholder="Optional. Anything this post should always do or never do."
            disabled={disabled}
            onChange={(note) => set((d) => ({ ...d, note }))}
          />
        )}
        {custom && (
          <>
            <TextField
              label="What to leave out"
              value={String(readPath(draft.body, "notRelevant") ?? "")}
              placeholder="nothing"
              disabled={disabled}
              onChange={(value) => setBody("notRelevant", value)}
            />
            <ApproachRows
              approach={readPath(draft.body, "approach") as HarperApproach | undefined}
              lastResolved={job.body?.lastResolved ?? null}
              disabled={disabled}
              onChange={(approach) => setBody("approach", approach)}
              groupByDeployNote={startsOnDeploy(draft.trigger) ? "A post on a production deploy groups by deploy." : null}
            />
            {previewNonce > 0 && (
              <PreviewCard
                deskId={ctx.deskId}
                jobId={job.id}
                current={previewInputs(draft.body)}
                saved={previewInputs(job.body as Record<string, unknown> | undefined)}
                canPreview={!ctx.readOnly}
                startNonce={previewNonce}
                neverRan={last === "never posted"}
                onStart={() => setPreviewNonce((n) => n + 1)}
                onSave={() => undefined}
              />
            )}
          </>
        )}
        {!ctx.readOnly && (
          <div className="acts" style={{ border: 0, paddingTop: 2, marginTop: 0 }}>
            {custom && (
              <Btn variant="quiet" size="sm" disabled={ctx.busy || missingInstructions} data-testid={`settings-post-preview-${job.id}`} onClick={() => setPreviewNonce((n) => n + 1)}>
                Preview the post
              </Btn>
            )}
            {canPost && (
              <Btn
                variant="quiet"
                size="sm"
                disabled={ctx.busy}
                onClick={() => void ctx.act(() => runJobNow(ctx.deskId, job.id), `${name} is posting now.`)}
              >
                Post it now
              </Btn>
            )}
            {custom && (
              <Btn
                variant="quiet"
                size="sm"
                style={{ color: "var(--incident)" }}
                disabled={ctx.busy}
                onClick={() => {
                  if (!window.confirm(`Remove ${name}? Its record stays on the Team.`)) return;
                  void ctx.act(() => deleteJob(ctx.deskId, job.id), `${name} is removed.`);
                }}
              >
                Remove
              </Btn>
            )}
          </div>
        )}
      </div>
    </details>
  );
}

function AddCustomPost({ ctx, slug }: { ctx: SettingsCtx; slug: string }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [instructions, setInstructions] = useState("");
  const [category, setCategory] = useState<WrittenAs>("other");
  const needs = writtenAsNeedsInstructions(category);
  const ready = name.trim() !== "" && (!needs || instructions.trim() !== "");

  if (!open) {
    return (
      <Field label="Add a post" top>
        <div className="tags">
          <button type="button" className="chip add" disabled={ctx.readOnly || ctx.busy} data-testid="settings-harper-custom" onClick={() => setOpen(true)}>
            + Custom post
          </button>
          <span className="fact" style={{ alignSelf: "center", fontSize: 11, marginLeft: 4 }}>
            The presets above are the posts Harper knows how to write well. A custom post runs on your instructions.
          </span>
        </div>
      </Field>
    );
  }
  return (
    <div className="addform" data-testid="settings-harper-custom-form">
      <TextField label="What it posts" value={name} placeholder="e.g. Starlink launch digest" maxLength={60} onChange={setName} />
      <SelectField
        label="Reports on"
        value={category}
        options={WRITTEN_AS.map((value) => ({ value, label: WRITTEN_AS_COPY[value].label }))}
        help={WRITTEN_AS_COPY[category].meaning}
        onChange={(value) => setCategory(normalizeWrittenAs(value, "other"))}
      />
      <AreaField
        label="Instructions"
        rows={2}
        value={instructions}
        placeholder={needs ? "What this post reports on, and how." : "Optional."}
        onChange={setInstructions}
      />
      <div className="acts" style={{ border: 0, paddingTop: 0 }}>
        <Btn variant="quiet" size="sm" onClick={() => setOpen(false)}>
          Cancel
        </Btn>
        <Btn
          variant="secondary"
          size="sm"
          disabled={!ready || ctx.busy}
          data-testid="settings-harper-custom-add"
          onClick={() =>
            void ctx
              .act(
                () =>
                  createJob(ctx.deskId, {
                    personaSlug: slug,
                    kind: "chronicler.custom",
                    instance: name.trim(),
                    body: { category, ...(instructions.trim() ? { instructions: instructions.trim() } : {}) },
                  }),
                `${name.trim()} is added. It posts on its rhythm.`,
              )
              .then((ok) => {
                if (!ok) return;
                setOpen(false);
                setName("");
                setInstructions("");
              })
          }
        >
          Add the post
        </Btn>
      </div>
    </div>
  );
}

export function HarperSection({ ctx }: { ctx: SettingsCtx }) {
  const agent = ctx.agentFor("chronicler");
  const words = AGENT_DEFAULTS.harper;
  const name = agent?.name ?? words.name;
  const engaged = ctx.page.groups.some((group) => group.craft === "chronicler") || (agent?.engaged ?? false);
  const posts = harperPosts(ctx.page.jobs);
  const mark = agent ? <Mark avatar={agent.avatar} name={name} slug={agent.personaSlug} size={30} /> : null;
  const slug = agent?.personaSlug ?? ctx.page.groups.find((group) => group.craft === "chronicler")?.personaSlug ?? "harper";
  const on = posts.filter((job) => ctx.line(job).active).length;
  const missingPresets = PRESET_KINDS.filter((kind) => !posts.some((job) => job.kind === kind));

  if (!engaged) {
    return (
      <section className="agentset dashed" id="set-harper" data-section="harper" data-testid="settings-harper">
        <div className="ah">
          {mark}
          <span style={{ minWidth: 0, flex: 1 }}>
            <div className="nm">{name}</div>
            <div className="role">{agent?.craftLabel ?? words.role}</div>
            <div className="sub">{words.sub}</div>
          </span>
          {agent && !ctx.readOnly && (
            <Btn
              variant="secondary"
              size="sm"
              disabled={ctx.busy}
              data-testid="settings-add-harper"
              onClick={() =>
                void ctx.act(() => engageAgent(ctx.deskId, agent.personaSlug), `${name} is on the Team.`)
              }
            >
              Add {name}
            </Btn>
          )}
        </div>
      </section>
    );
  }

  return (
    <AgentCard
      id="harper"
      mark={mark}
      name={name}
      role={words.role}
      sub={words.sub}
      headRight={<HeadFact>{`${posts.length} ${posts.length === 1 ? "post" : "posts"} · ${on} on`}</HeadFact>}
    >
      <div className="posts">
        {posts.map((job) => (
          <PostCard key={job.id} ctx={ctx} job={job} />
        ))}
      </div>
      {!ctx.readOnly && missingPresets.length > 0 && (
        <div className="tags">
          {missingPresets.map((kind) => (
            <button
              key={kind}
              type="button"
              className="chip add"
              disabled={ctx.busy}
              onClick={() =>
                void ctx.act(
                  () => createJob(ctx.deskId, { personaSlug: slug, kind, instance: "default" }),
                  `${PRESET_NAME[kind]} is added.`,
                )
              }
            >
              + {PRESET_NAME[kind]}
            </button>
          ))}
        </div>
      )}
      {!ctx.readOnly && <AddCustomPost ctx={ctx} slug={slug} />}
    </AgentCard>
  );
}
